// PropTypes & HOOKS
import PropTypes from 'prop-types'
import { useContext } from 'react'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { BrowserRouter as Router } from 'react-router-dom'

// HOOKS (Routing)
import { Switch, Route, Redirect } from 'wouter'

// HOOKS (Versió)
import useVersionCheck from 'hooks/useVersionCheck'

// COMPONENTS
import Layout from 'components/layout/Layout'
import NewVersionModal from 'components/ui/Modals/NewVersionModal'
import Login from 'pages/Login/Login'
import Logout from 'pages/Login/Logout'
import Dashboard from 'pages/Dashboard/Dashboard'
import SinglesViewGrid from 'pages/Singles/SinglesViewGrid'
import AdminViewGrid from 'pages/Admin/AdminViewGrid'
import MailsViewGrid from 'pages/Mails/MailsViewGrid'
import MailSplitViewGrid from 'pages/Mails/MailSplit/MailSplitViewGrid'
import SinglesViewDetail from 'pages/Singles/SinglesViewDetail'
import AdminViewDetail from 'pages/Admin/AdminViewDetail'
import MailsViewDetail from 'pages/Mails/MailsViewDetail'
import Students from 'pages/StudentsCalendar/StudentsCalendar'
import Holidays from 'pages/Holidays/Holidays'

// CONTEXTS
import { UserContext } from 'contexts/UserContext'
import SchoolsViewGrid from './Schools/SchoolViewGrid'
import SchoolsViewDetail from './Schools/SchoolViewDetails'
import StudentsViewGrid from './Students/StudentsViewGrid'
import StudentsViewDetails from './Students/StudentsViewDetails'
import TeachersViewGrid from './Teachers/TeacherViewGrid'
import TeacherViewDetail from './Teachers/TeacherViewDetails'
import StudentsCalendarPage from './StudentsCalendar/StudentsCalendarPage'
import StudentsCalendar from 'components/Students/StudentsCalendar0'
import MaintenancePage from './Mantainance'

// Rutas privadas que se pueden usar si se ha iniciado sesión:
const PrivateRoute = ({ isLoggedIn, isMaintainance, ...props }) =>
  isLoggedIn ? (
    isMaintainance ? (
      <MaintenancePage />
    ) : (
      <Layout>
        <Route {...props} />
      </Layout>
    )
  ) : (
    <Redirect to="/login" />
  )

// Ruta 'LOG IN' que se usa si no se ha iniciado sesión:
const LoginRoute = ({ isLoggedIn, ...props }) =>
  isLoggedIn ? <Redirect to="/" /> : <Route path="/login" component={Login} />

export default function App() {
  const { user } = useContext(UserContext)
  const isLoggedIn = user && !!user.token

    // Detecta si hi ha una versió nova del frontend desplegada
  const newVersion = useVersionCheck()

  // Activa o desactiva el mantenimiento globalmente
  const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true'

  // Emails exentos de mantenimiento
  const MAINTENANCE_EXEMPT_EMAILS =
    process.env.REACT_APP_MAINTENANCE_EXEMPT_EMAILS?.split(',')
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean) ?? []

  // Miramos si el usuario actual está exento
  const isExempt = MAINTENANCE_EXEMPT_EMAILS.includes(
    user?.email?.toLowerCase()
  )

  // Mostrar mantenimiento solo a role_id 1 no exentos
  const showMaintenance = MAINTENANCE_MODE && user?.role_id === 1 && !isExempt

  return (
    <Router>
      <>
      {/* Modal bloquejant si hi ha una nova versió desplegada */}
        {newVersion && <NewVersionModal />}
        <Switch>
          {/* <- LOG IN / LOG OUT */}
          <LoginRoute isLoggedIn={isLoggedIn} path="/login" component={Login} />

          <PrivateRoute
            isLoggedIn={isLoggedIn}
            path="/logout"
            component={Logout}
          />
          {/* LOG IN / LOG OUT -> */}

          {/* ######################################################################### */}

          {showMaintenance ? (
            <PrivateRoute
              isLoggedIn={isLoggedIn}
              path="/*"
              component={MaintenancePage}
              isMaintainance={true}
            />
          ) : (
            <>
              {/* <- PANEL */}
              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/"
                component={Dashboard}
              />
              {/* PANEL -> */}

              {/* ######################################################################### */}

              {/* <- CLIENTES */}
              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/singles/grid"
                component={SinglesViewGrid}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/alumnos"
                component={Students}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/fiestas"
                component={Holidays}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/schools/grid"
                component={SchoolsViewGrid}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/schools/:id"
                component={SchoolsViewDetail}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/students/grid"
                component={StudentsViewGrid}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/students/:id"
                component={StudentsViewDetails}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/teachers/grid"
                component={TeachersViewGrid}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/teachers/:id"
                component={TeacherViewDetail}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/calendar/:userId/:idEscola/:idPractiques"
                component={StudentsCalendarPage}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/singles/:Id/:idMail?/:registerSingle?"
                component={SinglesViewDetail}
              />
              {/* CLIENTES -> */}

              {/* ######################################################################### */}

              {/* <- FACTURAS */}
              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/mails/grid"
                component={MailsViewGrid}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/mails/:id"
                component={MailsViewDetail}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/mails/:idMail/mail-split"
                component={MailSplitViewGrid}
              />
              {/* FACTURAS -> */}

              {/* ######################################################################### */}

              {/* <- USUARIOS */}
              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/admin/grid"
                component={AdminViewGrid}
              />

              <PrivateRoute
                isLoggedIn={isLoggedIn}
                path="/admin/:id"
                component={AdminViewDetail}
              />
              {/* USUARIOS -> */}
            </>
          )}
        </Switch>

        <ToastContainer />
      </>
    </Router>
  )
}

PrivateRoute.propTypes = {
  isLoggedIn: PropTypes.bool,
  isMaintainance: PropTypes.bool
}

LoginRoute.propTypes = {
  isLoggedIn: PropTypes.bool
}
